import cn from "classnames";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BOARDS_INSIGHT_LOADING,
  BOARD_CARD_LOADING,
} from "../../constants/loading.constant";
import {
  ANALYTICS_EXPAND,
  BOARD_CARD_MODAL,
  BOARD_FILTERS_MODAL,
  BOARD_METRIC_MODAL,
} from "../../constants/modal.constant";
import { useBoardAnalyticsCore } from "../../cores/boardAnalytics";
import { ChartMetric } from "../../cores/boardAnalytics/interface/ChartMetric";
import {
  processTotalsMetric,
  toLineMetric,
  toPieMetric,
} from "../../cores/boardAnalytics/utils/processTotalsMetric";
import {
  CrmAnalyticsLayout,
  CRM_ANALYTICS_DEFAULT_ORDER,
  loadCrmAnalyticsLayout,
  reorderCrmAnalyticsWidgets,
  resetCrmAnalyticsLayout,
  saveCrmAnalyticsLayout,
} from "../../cores/boardAnalytics/utils/crmAnalyticsLayout";
import { useBoardCore } from "../../cores/boards";
import { getCurrencySymbol } from "../../cores/boards/constants/currencies.constant";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { CrmChartCard } from "../component/analytics/CrmChartCard";
import { CrmChartLegend } from "../component/analytics/CrmChartLegend";
import { CrmKpiRow } from "../component/analytics/CrmKpiRow";
import { BarChart } from "../component/analytics/charts/BarChart";
import { CrmDonutChart } from "../component/analytics/charts/CrmDonutChart";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useIntl } from "react-intl";
import { CL$Format } from "../../cores/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BoardSelector from "../component/boards/BoardSelector";
import FiltersForm from "../component/boards/modal/FiltersForm";
import Opportunities from "../component/boards/modal/Opportunities";
import moment from "moment";
import TaskSideModals from "../component/tasks/TaskSideModals";
import BoardSideModals from "../component/boards/BoardSideModals";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import ContactSideModals from "../component/contacts/ContactSideModals";
import EventSideModals from "../component/event/EventSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import { CARD_PRODUCT } from "../../constants/internalModal.constant";
import { Errors } from "../../constants/errors";
import { CardStatusOptions } from "../../cores/boards/constants/comment-type";
import { useConfigCore } from "../../cores/config";
import { InputWembii } from "../component/InputWembii";
import { CheckboxWembii } from "../component/CheckboxWembii";
import { TaskStatus } from "../../cores/tasks/config";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import "../component/analytics/CrmMetrics.scss";
import { WEMBII_BRAND } from "../component/analytics/crmAnalytics.constants";

const percentFormat = (value: number) => `${value}%`;

type ChartWidget = {
  id: string;
  title: string;
  variant: "bar" | "donut";
  badge?: string;
  className?: string;
  isEmpty: boolean;
  selector?: ReactNode;
  legend?: ReactNode;
  body: ReactNode;
};

export const BusinessBoardAnalyticsScreen = () => {
  const intl = useIntl();
  const { company, companyPlan, companyRole, isAdmin, isReseller } =
    useMeCore();
  const {
    isModal,
    turnOnModal,
    turnOffModal,
    getModalVisibility,
    turnOffInternalModal,
    isLoading,
    getModalContent,
  } = useGlobalCore();
  const { goToSocial } = useRouterCore();
  const { commentTypes, getCommentTypes } = useConfigCore();
  const {
    boardBusiness: board,
    filters,
    errorCode,
    getBusinessBoards,
    cleanCard,
    getFilters,
    getBusinessBoard,
  } = useBoardCore();
  const {
    getCrmInsights,
    toChartMetric,
    setDate,
    setHasMore,
    date,
    crmInsights,
  } = useBoardAnalyticsCore();
  const [commentType, setCommentType] = useState("all");
  const [taskType, setTaskType] = useState("all");
  const [layout, setLayout] = useState<CrmAnalyticsLayout>(() =>
    loadCrmAnalyticsLayout(company?.id, board?.id)
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  useEffect(() => {
    if (
      !companyPlan?.rules.allowBusinessBoard ||
      (!companyRole?.crm &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    getBusinessBoards(company?.id!);
    getCommentTypes(company?.id!);
  }, [company, getBusinessBoards, getCommentTypes]);

  useEffect(() => {
    setLayout(loadCrmAnalyticsLayout(company?.id, board?.id));
  }, [company?.id, board?.id]);

  useEffect(() => {
    if (!company?.id || !board?.id) return;
    saveCrmAnalyticsLayout(company.id, board.id, layout);
  }, [company?.id, board?.id, layout]);

  const cardByDayData = useMemo(
    () => toLineMetric(crmInsights.cardsByDay),
    [crmInsights.cardsByDay]
  );
  const cardsByListData = useMemo(
    () => toPieMetric(crmInsights.cardsByList),
    [crmInsights.cardsByList]
  );
  const cardsByStatusData = useMemo(
    () => toPieMetric(crmInsights.cardsByStatus),
    [crmInsights.cardsByStatus]
  );
  const budgetByListData = useMemo(
    () => toPieMetric(crmInsights.budgetByList, CL$Format),
    [crmInsights.budgetByList]
  );
  const budgetByStatusData = useMemo(
    () => toPieMetric(crmInsights.budgetByStatus, CL$Format),
    [crmInsights.budgetByStatus]
  );
  const cardsByUserData = useMemo(
    () => toPieMetric(crmInsights.cardsByUser),
    [crmInsights.cardsByUser]
  );
  const averageBudgetByListData = useMemo(
    () => toPieMetric(crmInsights.averageBudgetByList, CL$Format),
    [crmInsights.averageBudgetByList]
  );
  const winRateByListData = useMemo(
    () => toPieMetric(crmInsights.winRateByList, percentFormat),
    [crmInsights.winRateByList]
  );
  const dealExpirationData = useMemo(
    () => toPieMetric(crmInsights.dealExpiration),
    [crmInsights.dealExpiration]
  );
  const activitiesByDayData = useMemo(
    () => toLineMetric(crmInsights.activitiesByDay),
    [crmInsights.activitiesByDay]
  );
  const commentsByUserData = useMemo(
    () => processTotalsMetric(crmInsights.commentsByUser, commentType),
    [crmInsights.commentsByUser, commentType]
  );
  const tasksByUserData = useMemo(
    () => processTotalsMetric(crmInsights.tasksByUser, taskType),
    [crmInsights.tasksByUser, taskType]
  );

  const cardByDayChartMetrics: ChartMetric = useMemo(
    () => toChartMetric([cardByDayData]),
    [cardByDayData, toChartMetric]
  );
  const activitiesByDayChartMetrics: ChartMetric = useMemo(
    () => toChartMetric([activitiesByDayData]),
    [activitiesByDayData, toChartMetric]
  );

  const typesValues = useMemo(
    () => [
      { value: "all", label: intl.formatMessage({ id: "app.source.all" }) },
      ...CardStatusOptions.map((status) => ({
        value: status.value,
        label: intl.formatMessage({ id: status.label }),
      })),
      ...commentTypes.map((type) => ({ value: type.id, label: type.name })),
    ],
    [commentTypes, intl]
  );

  const taskTypesValues = useMemo(
    () => [
      { value: "all", label: intl.formatMessage({ id: "app.source.all" }) },
      {
        value: TaskStatus.Completed,
        label: intl.formatMessage({ id: "app.tasks.completed" }),
      },
      {
        value: TaskStatus.Incompleted,
        label: intl.formatMessage({ id: "app.tasks.incompleted" }),
      },
    ],
    [intl]
  );

  const toggleFilterModal = useCallback(() => {
    turnOnModal(
      BOARD_FILTERS_MODAL,
      <FiltersForm
        board={board}
        onFilter={(filters, onSuccess) => {
          if (board) {
            getCrmInsights(company?.id!, board.id, filters, onSuccess);
          }
        }}
        onClearFilter={() => {
          if (board) {
            getCrmInsights(company?.id!, board.id, {});
          }
        }}
      />
    );
  }, [turnOnModal, getCrmInsights, company, board]);

  const onSearching = useCallback(
    (companyId: string, boardId: string) => {
      const filter = getFilters(boardId);
      getBusinessBoard(companyId, boardId, filter, false);
      getCrmInsights(companyId, boardId, filter);
    },
    [getCrmInsights, getFilters, getBusinessBoard]
  );

  const isFiltered = useMemo(() => {
    return filters[`${board?.id!}`];
  }, [filters, board]);

  const showBarCards = useCallback(
    (element) => {
      if (!element.length) return;
      const metricDate = cardByDayChartMetrics.labels[element[0]._index];

      if (date !== metricDate) {
        setDate(metricDate);
      }

      setHasMore(true);
      turnOnModal(BOARD_METRIC_MODAL, true, Position.LEFT);
    },
    [cardByDayChartMetrics, date, setDate, setHasMore, turnOnModal]
  );

  const totalOpportunitiesBadge = useMemo(() => {
    const total = crmInsights.crmSummary?.values?.find(
      (v) => v.label === "totalOpportunities"
    )?.value;
    if (typeof total !== "number") return undefined;
    return String(
      intl.formatMessage(
        { id: "app.metricsCrm.totalBadge" },
        { count: total }
      )
    );
  }, [crmInsights.crmSummary, intl]);

  const formatBarLabels = useCallback(
    (labels: string[]) =>
      labels.map((dateLabel) => {
        const parsed = moment(dateLabel);
        if (!parsed.isValid()) return dateLabel;
        return parsed.format("DD MMM");
      }),
    []
  );

  const widgetsById = useMemo(() => {
    const map: Record<string, ChartWidget> = {
      crmChartOpportunitiesByDay: {
        id: "crmChartOpportunitiesByDay",
        title: intl.formatMessage({ id: "app.metricsCrm.oportunitys" }),
        badge: totalOpportunitiesBadge,
        variant: "bar",
        className: "crm-chart-card--compact",
        isEmpty: !cardByDayData.values || cardByDayData.values.length === 0,
        body: (
          <BarChart
            labels={formatBarLabels(cardByDayChartMetrics.labels)}
            datasets={cardByDayChartMetrics.datasets.map((dataset) => ({
              ...dataset,
              color: WEMBII_BRAND,
            }))}
            onElementClick={showBarCards}
            height={180}
          />
        ),
      },
      crmChartActivitiesByDay: {
        id: "crmChartActivitiesByDay",
        title: intl.formatMessage({ id: "app.metricsCrm.activitiesByDay" }),
        variant: "bar",
        className: "crm-chart-card--compact",
        isEmpty:
          !activitiesByDayData.values ||
          activitiesByDayData.values.length === 0,
        body: (
          <BarChart
            labels={formatBarLabels(activitiesByDayChartMetrics.labels)}
            datasets={activitiesByDayChartMetrics.datasets.map((dataset) => ({
              ...dataset,
              color: WEMBII_BRAND,
            }))}
            height={180}
          />
        ),
      },
      cardsByList: {
        id: "cardsByList",
        title: intl.formatMessage({ id: "app.metricsCrm.oportunitys1" }),
        variant: "donut",
        isEmpty: !cardsByListData.values || cardsByListData.values.length === 0,
        legend: <CrmChartLegend metric={cardsByListData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([cardsByListData]).labels}
            datasets={toChartMetric([cardsByListData]).datasets}
          />
        ),
      },
      cardsByStatus: {
        id: "cardsByStatus",
        title: intl.formatMessage({ id: "app.metricsCrm.cardsByStatus" }),
        variant: "donut",
        isEmpty:
          !cardsByStatusData.values || cardsByStatusData.values.length === 0,
        legend: <CrmChartLegend metric={cardsByStatusData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([cardsByStatusData]).labels}
            datasets={toChartMetric([cardsByStatusData]).datasets}
          />
        ),
      },
      cardsByUser: {
        id: "cardsByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.cardsByUser" }),
        variant: "donut",
        isEmpty: !cardsByUserData.values || cardsByUserData.values.length === 0,
        legend: <CrmChartLegend metric={cardsByUserData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([cardsByUserData]).labels}
            datasets={toChartMetric([cardsByUserData]).datasets}
          />
        ),
      },
      budgetByList: {
        id: "budgetByList",
        title: `${intl.formatMessage({
          id: "app.metricsCrm.oportunitys2",
        })} (${board?.currency && getCurrencySymbol(board.currency)})`,
        variant: "donut",
        isEmpty:
          !budgetByListData.values || budgetByListData.values.length === 0,
        legend: <CrmChartLegend metric={budgetByListData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([budgetByListData]).labels}
            datasets={toChartMetric([budgetByListData]).datasets}
          />
        ),
      },
      budgetByStatus: {
        id: "budgetByStatus",
        title: intl.formatMessage({ id: "app.metricsCrm.budgetByStatus" }),
        variant: "donut",
        isEmpty:
          !budgetByStatusData.values || budgetByStatusData.values.length === 0,
        legend: <CrmChartLegend metric={budgetByStatusData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([budgetByStatusData]).labels}
            datasets={toChartMetric([budgetByStatusData]).datasets}
          />
        ),
      },
      averageBudgetByList: {
        id: "averageBudgetByList",
        title: intl.formatMessage({ id: "app.metricsCrm.averageBudgetByList" }),
        variant: "donut",
        isEmpty:
          !averageBudgetByListData.values ||
          averageBudgetByListData.values.length === 0,
        legend: <CrmChartLegend metric={averageBudgetByListData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([averageBudgetByListData]).labels}
            datasets={toChartMetric([averageBudgetByListData]).datasets}
          />
        ),
      },
      winRateByList: {
        id: "winRateByList",
        title: intl.formatMessage({ id: "app.metricsCrm.winRateByList" }),
        variant: "donut",
        isEmpty:
          !winRateByListData.values || winRateByListData.values.length === 0,
        legend: <CrmChartLegend metric={winRateByListData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([winRateByListData]).labels}
            datasets={toChartMetric([winRateByListData]).datasets}
          />
        ),
      },
      dealExpiration: {
        id: "dealExpiration",
        title: intl.formatMessage({ id: "app.metricsCrm.dealExpiration" }),
        variant: "donut",
        isEmpty:
          !dealExpirationData.values || dealExpirationData.values.length === 0,
        legend: <CrmChartLegend metric={dealExpirationData} />,
        body: (
          <CrmDonutChart
            labels={toChartMetric([dealExpirationData]).labels}
            datasets={toChartMetric([dealExpirationData]).datasets}
          />
        ),
      },
      commentsByUser: {
        id: "commentsByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.oportunitys3" }),
        variant: "donut",
        isEmpty:
          !commentsByUserData.values || commentsByUserData.values.length === 0,
        legend: <CrmChartLegend metric={commentsByUserData} />,
        selector: (
          <InputWembii
            type="select"
            onChange={setCommentType}
            value={commentType}
            mb={false}
          >
            {typesValues.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </InputWembii>
        ),
        body: (
          <CrmDonutChart
            labels={toChartMetric([commentsByUserData]).labels}
            datasets={toChartMetric([commentsByUserData]).datasets}
          />
        ),
      },
      tasksByUser: {
        id: "tasksByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.tasksByUser" }),
        variant: "donut",
        isEmpty: !tasksByUserData.values || tasksByUserData.values.length === 0,
        legend: <CrmChartLegend metric={tasksByUserData} />,
        selector: (
          <InputWembii
            type="select"
            onChange={setTaskType}
            value={taskType}
            mb={false}
          >
            {taskTypesValues.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </InputWembii>
        ),
        body: (
          <CrmDonutChart
            labels={toChartMetric([tasksByUserData]).labels}
            datasets={toChartMetric([tasksByUserData]).datasets}
          />
        ),
      },
    };
    return map;
  }, [
    activitiesByDayChartMetrics,
    activitiesByDayData,
    averageBudgetByListData,
    board?.currency,
    budgetByListData,
    budgetByStatusData,
    cardByDayChartMetrics,
    cardByDayData,
    cardsByListData,
    cardsByStatusData,
    cardsByUserData,
    commentType,
    commentsByUserData,
    dealExpirationData,
    formatBarLabels,
    intl,
    showBarCards,
    taskType,
    taskTypesValues,
    tasksByUserData,
    toChartMetric,
    totalOpportunitiesBadge,
    typesValues,
    winRateByListData,
  ]);

  const widgetCatalog = useMemo(
    () =>
      CRM_ANALYTICS_DEFAULT_ORDER.map((id) => ({
        id,
        title: widgetsById[id]?.title || id,
      })),
    [widgetsById]
  );

  const visibleWidgets = useMemo(
    () =>
      layout.order
        .filter((id) => !layout.hidden.includes(id))
        .map((id) => widgetsById[id])
        .filter(Boolean),
    [layout.hidden, layout.order, widgetsById]
  );

  const toggleMinimize = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      minimized: prev.minimized.includes(id)
        ? prev.minimized.filter((item) => item !== id)
        : [...prev.minimized, id],
    }));
  }, []);

  const toggleHidden = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      hidden: prev.hidden.includes(id)
        ? prev.hidden.filter((item) => item !== id)
        : [...prev.hidden, id],
    }));
  }, []);

  const showAllWidgets = useCallback(() => {
    setLayout((prev) => ({ ...prev, hidden: [] }));
  }, []);

  const resetLayout = useCallback(() => {
    setLayout(resetCrmAnalyticsLayout(company?.id, board?.id));
  }, [board?.id, company?.id]);

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLElement>, id: string) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", id);
      setDraggingId(id);
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>, id: string) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dragOverId !== id) setDragOverId(id);
    },
    [dragOverId]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, toId: string) => {
      event.preventDefault();
      const fromId =
        event.dataTransfer.getData("text/plain") || draggingId || "";
      if (!fromId) return;
      setLayout((prev) => ({
        ...prev,
        order: reorderCrmAnalyticsWidgets(prev.order, fromId, toId),
      }));
      setDraggingId(null);
      setDragOverId(null);
    },
    [draggingId]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.metricsCrm.title" })}
        rightChildren={
          <>
            <BoardSelector onSearching={onSearching} />
            <UncontrolledDropdown className="ml-2">
              <DropdownToggle
                caret
                className="whiteButton d-flex align-items-center"
              >
                <FontAwesomeIcon icon={["fas", "th-large"]} className="mr-2" />
                {intl.formatMessage({ id: "app.metricsCrm.layout.customize" })}
              </DropdownToggle>
              <DropdownMenu right className="crm-analytics-customize-menu">
                <DropdownItem header>
                  {intl.formatMessage({
                    id: "app.metricsCrm.layout.visibleCharts",
                  })}
                </DropdownItem>
                {widgetCatalog.map(({ id, title }) => (
                  <div key={id} className="crm-analytics-customize-menu__item">
                    <CheckboxWembii
                      id={`crm-analytics-vis-${id}`}
                      mb={false}
                      checked={!layout.hidden.includes(id)}
                      label={title}
                      onChange={() => toggleHidden(id)}
                    />
                  </div>
                ))}
                <DropdownItem divider />
                <DropdownItem onClick={showAllWidgets}>
                  {intl.formatMessage({
                    id: "app.metricsCrm.layout.showAll",
                  })}
                </DropdownItem>
                <DropdownItem onClick={resetLayout}>
                  {intl.formatMessage({
                    id: "app.metricsCrm.layout.reset",
                  })}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <button
              className={cn("ml-2 btn", {
                whiteButton: !isFiltered,
                "btn-primary": isFiltered,
              })}
              onClick={toggleFilterModal}
            >
              <FontAwesomeIcon icon={["fas", "sliders-h"]} className="mr-2" />
              {intl.formatMessage({ id: "app.crmBusiness.filters" })}
            </button>
          </>
        }
      />
      {isLoading(BOARDS_INSIGHT_LOADING) ? (
        <div className="crm-metrics-loading">
          <UpdateLoadingCard
            loadingId={BOARDS_INSIGHT_LOADING}
            text={intl.formatMessage({ id: "app.metricsCrm.updating" })}
          />
          <p className="crm-metrics-loading__text">
            {intl.formatMessage({ id: "app.metricsCrm.updatingDescription" })}
          </p>
        </div>
      ) : (
        <Content>
          <div className="crm-metrics-page">
            <section className="crm-metrics-section">
              <h2 className="crm-metrics-section__title">
                {intl.formatMessage({ id: "app.metricsCrm.sectionOverview" })}
              </h2>
              <CrmKpiRow
                summary={crmInsights.crmSummary}
                currency={board?.currency}
              />
            </section>

            <section className="crm-metrics-section">
              <div className="crm-metrics-section__head">
                <h2 className="crm-metrics-section__title mb-0">
                  {intl.formatMessage({ id: "app.metricsCrm.sectionCharts" })}
                </h2>
                <p className="crm-metrics-section__hint mb-0">
                  {intl.formatMessage({ id: "app.metricsCrm.layout.hint" })}
                </p>
              </div>

              {visibleWidgets.length === 0 ? (
                <div className="crm-analytics-empty-layout">
                  <p className="mb-2">
                    {intl.formatMessage({
                      id: "app.metricsCrm.layout.empty",
                    })}
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={showAllWidgets}
                  >
                    {intl.formatMessage({
                      id: "app.metricsCrm.layout.showAll",
                    })}
                  </button>
                </div>
              ) : (
                <div className="crm-charts-grid crm-charts-grid--customizable">
                  {visibleWidgets.map((widget) => (
                    <CrmChartCard
                      key={widget.id}
                      id={widget.id}
                      title={widget.title}
                      badge={widget.badge}
                      variant={widget.variant}
                      className={widget.className}
                      isEmpty={widget.isEmpty}
                      selector={widget.selector}
                      legend={widget.legend}
                      minimized={layout.minimized.includes(widget.id)}
                      onToggleMinimize={() => toggleMinimize(widget.id)}
                      draggable
                      isDragging={draggingId === widget.id}
                      isDragOver={dragOverId === widget.id}
                      onDragStart={(event) =>
                        handleDragStart(event, widget.id)
                      }
                      onDragOver={(event) => handleDragOver(event, widget.id)}
                      onDrop={(event) => handleDrop(event, widget.id)}
                      onDragEnd={handleDragEnd}
                    >
                      {widget.body}
                    </CrmChartCard>
                  ))}
                </div>
              )}
            </section>
          </div>

          <SideModal
            active={isModal(ANALYTICS_EXPAND)}
            visible={getModalVisibility(ANALYTICS_EXPAND)}
            onClose={() => turnOffModal(ANALYTICS_EXPAND)}
            left
          >
            {getModalContent(ANALYTICS_EXPAND)}
          </SideModal>
          <SideModal
            active={isModal(BOARD_FILTERS_MODAL)}
            visible={getModalVisibility(BOARD_FILTERS_MODAL)}
            onClose={() => turnOffModal(BOARD_FILTERS_MODAL)}
          >
            {getModalContent(BOARD_FILTERS_MODAL)}
          </SideModal>
          <SideModal
            active={isModal(BOARD_METRIC_MODAL)}
            visible={getModalVisibility(BOARD_METRIC_MODAL)}
            onClose={() => {
              turnOffModal(BOARD_METRIC_MODAL);
              setHasMore(false);
            }}
            left
          >
            <Opportunities />
          </SideModal>
          <SideModal
            active={isModal(BOARD_CARD_MODAL)}
            visible={getModalVisibility(BOARD_CARD_MODAL)}
            onClose={() => {
              turnOffModal(BOARD_CARD_MODAL);
              turnOffInternalModal(CARD_PRODUCT);
              cleanCard();
            }}
            showSppiner={isLoading(BOARD_CARD_LOADING)}
            showError={errorCode === Errors.CardLoad}
          >
            {getModalContent(BOARD_CARD_MODAL)}
          </SideModal>

          <TaskSideModals />
          <ContactSideModals />
          <CompanySideModals />
          <BoardSideModals />
          <EventSideModals />
          <EmailSideModals />
        </Content>
      )}
    </Layout>
  );
};
